import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Clock, ArrowLeft, Trash2 } from 'lucide-react'
import { contentApi, type ContentListItem } from '../../lib/api'
import SectionBanner from '../../components/SectionBanner'
import ContentCard from '../../components/ContentCard'
import EmptyState from '../../components/EmptyState'
import { usePageTitle } from '../../hooks/usePageTitle'

const PAGE_SIZE = 20

type HistoryItem = ContentListItem & { last_viewed_at?: string; read_percent?: number }

const History = () => {
  usePageTitle()
  const [items, setItems] = useState<HistoryItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [clearing, setClearing] = useState(false)

  const load = async () => {
    setLoading(true); setError('')
    try {
      const res = await contentApi.listHistory({ page, page_size: PAGE_SIZE })
      setItems(res.items as HistoryItem[])
      setTotal(res.total)
    } catch (e: any) {
      setError(e.message || '加载失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [page])

  const onClear = async () => {
    if (!confirm('确定要清空全部阅读历史吗？')) return
    setClearing(true)
    try {
      await contentApi.clearHistory()
      setPage(1)
      load()
    } catch (e) {
      console.error('Clear history failed', e)
    } finally {
      setClearing(false)
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)
  const formatTime = (s?: string) => s ? new Date(s).toLocaleString('zh-CN', { hour12: false }) : ''

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      <SectionBanner title="阅读历史" subtitle="最近看过的健康科普">
        <Link to="/user/knowledge" className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 mt-1">
          <ArrowLeft className="w-3 h-3" />返回科普列表
        </Link>
      </SectionBanner>

      <div className="px-4 -mt-6 relative z-10">
        {error ? (
          <EmptyState variant="error" description={error} />
        ) : loading ? (
          <EmptyState variant="loading" />
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md">
            <EmptyState
              title="还没有阅读历史"
              description="去阅读感兴趣的科普文章吧"
              action={{ label: '浏览科普', onClick: () => window.location.href = '/user/knowledge' }}
            />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <Clock className="w-3 h-3" />
                <span>共 {total} 条记录</span>
              </div>
              {total > 0 && (
                <button
                  onClick={onClear}
                  disabled={clearing}
                  className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1 disabled:opacity-40"
                >
                  <Trash2 className="w-3 h-3" />清空全部
                </button>
              )}
            </div>
            <div className="space-y-2">
              {items.map(item => (
                <div key={item.id} className="bg-white rounded-2xl shadow-md p-3">
                  <ContentCard item={item} linkPrefix="/user/knowledge" variant="compact" />
                  <div className="flex items-center justify-between mt-2 px-1 text-[10px] text-gray-400">
                    <span>{formatTime(item.last_viewed_at)}</span>
                    {item.read_percent !== undefined && item.read_percent > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <span className="w-16 h-1 bg-gray-100 rounded-full overflow-hidden">
                          <span className="h-full bg-green-500 inline-block" style={{ width: `${item.read_percent}%` }} />
                        </span>
                        <span>{item.read_percent}%</span>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 text-sm pt-4">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  className="px-4 py-1.5 bg-white border rounded-full disabled:opacity-40 hover:bg-gray-50"
                >上一页</button>
                <span className="text-gray-500">{page} / {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="px-4 py-1.5 bg-white border rounded-full disabled:opacity-40 hover:bg-gray-50"
                >下一页</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default History
